/**
 * BSCPF-6584. The client-side mirror of the API's `SupervisorContact`.
 *
 * Two screens have to agree on one question — "did this edit move the request to a
 * different person?" — because the answer decides whether the applicant's existing
 * answers are thrown away:
 *
 * - the edit modal warns the PM before saving;
 * - the activity log strikes the old contact through after.
 *
 * The server is the one that actually decides, so these normalizers reproduce what it
 * does rather than inventing a rule: it lowercases and trims an incoming email before
 * storing or comparing it, and stores phones as bare E.164 digits. Comparing raw strings
 * made a case-only email edit, or a retyped-but-identical number, read as a new
 * recipient and warn about a clear the server would never perform.
 */

/** Matches the API's `raw.to_s.strip.downcase`. */
export const normalizedEmail = (value: string | undefined | null) =>
  (value ?? "").trim().toLowerCase();

/**
 * The API stores `12145550123`; this form — and `validatePhoneNumberFormat` — speak the
 * 10-digit national number. Comparing either shape against the other is what made the
 * modal refuse to save a name-only fix for every supervisor with a phone on file.
 */
export const nationalDigits = (value: string | undefined | null) => {
  const digits = (value ?? "").replace(/\D/g, "");

  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

// Not exported: it only names the shape `recipientChanged` compares, and an exported
// type nothing imports is what Knip reports.
interface SupervisorContactFields {
  email?: string | null;
  phone?: string | null;
}

/**
 * True when the email or the phone would reach someone else. A corrected name is not a
 * new recipient and must not cost the answers.
 */
export const recipientChanged = (
  before: SupervisorContactFields | undefined,
  after: SupervisorContactFields | undefined
) =>
  normalizedEmail(before?.email) !== normalizedEmail(after?.email) ||
  nationalDigits(before?.phone) !== nationalDigits(after?.phone);
